import { AsyncPipe, NgIf, NgStyle } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { CouponInfoQr, QRCodeSettings } from "@app/models";
import { CouponService } from "@app/services";
import { UpgradeableItemComponent } from "@app/shared-components";
import { CouponPreviewModalComponent } from "@app/shared/components/coupon-preview-modal";
import { NgbModal, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslocoModule } from "@ngneat/transloco";
import { PermissionTypeDto } from "clearline-api";
import { alertApiError } from "core-kit";
import { BehaviorSubject } from "rxjs";
import { filter, finalize, take, tap } from "rxjs/operators";
import { ToastService } from "ui-kit";

export interface CouponConfig {
  hasEdit?: boolean;
  isEditDisabled?: boolean;
  promoPermission?: PermissionTypeDto;
}

@Component({
  selector: "app-coupon",
  templateUrl: "./coupon.component.html",
  styleUrls: ["./coupon.component.scss"],
  standalone: true,
  imports: [NgIf, AsyncPipe, NgStyle, NgbTooltip, TranslocoModule, UpgradeableItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CouponComponent {
  @Input() viewType: "simple" | "full" = "full";
  @Input() sizeType: "standard" | "small" = "standard";
  @Input() selectionType: "single" | "multiple" = "multiple";
  @Input() withPreview = true;
  @Input() config: CouponConfig = { hasEdit: false, isEditDisabled: false };
  @Input() index: number;
  @Input() previewMode = false;
  @Input() item: CouponInfoQr;
  @Input() selectedCoupon: CouponInfoQr;
  @Input() visible = true;
  @Input() disabledNotActive: boolean;
  @Input() removeSelected = false;

  @Output() selectCoupon = new EventEmitter<CouponInfoQr>();
  @Output() closeQrCodeModal = new EventEmitter<null>();
  @Output() upgradeClick = new EventEmitter<PermissionTypeDto>();

  loading = true;
  qrCodeSettings: QRCodeSettings = null;
  isEditingInProgress$ = new BehaviorSubject<boolean>(false);

  get isActive() {
    return this.item && this.item.status === "active";
  }

  constructor(private modalSvc: NgbModal, private couponService: CouponService, private toastService: ToastService) {}

  singleSelectCoupon(coupon: CouponInfoQr): void {
    if (!!this.config?.promoPermission) {
      return;
    }

    const canSelect = (this.selectionType === "single" && !this.disabledNotActive) || (this.disabledNotActive && this.isActive);

    if (!canSelect) {
      return;
    }

    if (this.removeSelected && this.selectedCoupon?.id === coupon.id) {
      this.selectCoupon.emit(null);
      return;
    }

    this.selectCoupon.emit(coupon);
  }

  multipleSelectCoupon(coupon: CouponInfoQr): void {
    if (this.viewType === "full" && this.selectionType === "multiple") {
      this.selectCoupon.emit(coupon);
    }
  }

  preview(coupon: CouponInfoQr) {
    const featureModal = this.modalSvc.open(CouponPreviewModalComponent, {
      backdrop: "static",
      size: "lg",
      centered: true
    });

    const comp: CouponPreviewModalComponent = featureModal.componentInstance;
    comp.item = coupon;
  }

  onEdit(event: MouseEvent): void {
    event.stopPropagation();

    if (this.config.hasEdit && !this.config.isEditDisabled && !this.config?.promoPermission) {
      this.isEditingInProgress$.next(true);

      this.couponService
        .getCouponExternalEditUrl(this.item.id)
        .pipe(
          alertApiError(this.toastService),
          filter((url: string) => !!url),
          tap((url: string) => window.open(url, "_blank")),
          take(1),
          finalize(() => this.isEditingInProgress$.next(false))
        )
        .subscribe();
    }
  }

  onUpgradeClick(permission: PermissionTypeDto): void {
    this.upgradeClick.emit(permission);
  }
}
